import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PerformanceIncentive {
  id: string;
  employee_id: string;
  incentive_type: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  period_start?: string;
  period_end?: string;
  related_goal_id?: string;
  status: string;
  approved_by?: string;
  approved_at?: string;
  paid_at?: string;
  payment_method?: string;
  payment_reference?: string;
  notes?: string;
  metadata?: Record<string, any>;
  created_at: string;
  employees?: {
    id: string;
    name: string;
  };
  related_goal?: {
    id: string;
    title: string;
  };
}

export const usePerformanceIncentives = (employeeId?: string, status?: string) => {
  return useQuery({
    queryKey: ["performance-incentives", employeeId, status],
    queryFn: async () => {
      let query = supabase
        .from("performance_incentives")
        .select(`
          *,
          employees!performance_incentives_employee_id_fkey(id, name),
          related_goal:performance_goals(id, title)
        `)
        .order("created_at", { ascending: false });

      if (employeeId) {
        query = query.eq("employee_id", employeeId);
      }

      if (status) {
        query = query.eq("status", status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as PerformanceIncentive[];
    },
  });
};

export const useCreateIncentive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (incentive: any) => {
      const { data, error } = await supabase
        .from("performance_incentives")
        .insert([incentive])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["performance-incentives"] });
      toast.success("تم إنشاء الحافز بنجاح");
    },
    onError: (error: any) => {
      toast.error("فشل إنشاء الحافز: " + error.message);
    },
  });
};

export const useUpdateIncentive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PerformanceIncentive> }) => {
      const { data: result, error } = await supabase
        .from("performance_incentives")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["performance-incentives"] });
      toast.success("تم تحديث الحافز بنجاح");
    },
    onError: (error: any) => {
      toast.error("فشل تحديث الحافز: " + error.message);
    },
  });
};